// @flow
import React from 'react';
import styled from 'styled-components';
import { Formik, Form } from 'formik';
import * as RenterApp from 'styles/Home/StyledRenterApplication';
import ErrorFocus from 'components/shared/ErrorFocus';
import * as Yup from 'yup';
import * as Layout from 'styles/Layout';
import StateStore from 'stores/StateStore';
import StatesAction from 'actions/StateActions';
import LegalEntity from 'models/LegalEntity';
import Spinner from 'components/shared/Spinner';

const Subtitle = styled(RenterApp.Subtitle)`
  font-size: 14px;
  margin-bottom: 4px;
`;

const DataConfirmText = styled.div`
  height: 22px;
  color: #4a4a4a;
  font-size: 18px;
  font-weight: bold;
  line-height: 22px;
  text-align: center;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const LineHr = styled.div`
  box-sizing: border-box;
  height: 2px;
  width: 300px;
  border: 1px solid #e9e9e9;
  margin-bottom: 30px;
  display: ${props => (props.lineVisible ? 'block' : 'none')};
`;

const ContentForm = styled.div`
  display: ${props => (props.visible ? 'block' : 'none')};
  width: 300px;
  margin-left: calc(50% - 150px);
`;

const FlatButton = styled.div`
  display: block;
  cursor: pointer;
  height: 17px;
  width: 113px;
  text-align: center;
  color: #278c7e;
  font-size: 14px;
  line-height: 17px;
  margin: 3px auto 5px auto;
`;

const Button = styled(RenterApp.SubmitButton)`
  background-color: ${({ color }) => color} !important;
`;

const rfcRegExp = /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/;

type Props = {
  visible: boolean,
  lineVisible: boolean,
  handleClickBillAction: ({}) => void,
  legalEntity: LegalEntity,
  handleClickNotBillAction: () => void,
  showNotBilling?: boolean,
  spinnerVisible?: boolean,
  color?: string
};

type StateType = {
  states: []
};

export default class BillDataForm extends React.Component<Props, StateType> {
  static defaultProps = {
    color: '#278C7E'
  };

  state = {
    states: []
  };

  defaultValues = (): {} => {
    const { legalEntity } = this.props;
    const address = legalEntity.address || {};
    return {
      business_name: legalEntity.business_name || '',
      rfc: legalEntity.rfc || '',
      street: address.street || '',
      ext_number: address.ext_number || '',
      int_number: address.int_number || '',
      neighborhood: address.neighborhood || '',
      state: address.state || '',
      town: address.town || '',
      district: address.district || '',
      country: address.country || '',
      zip: address.zip || ''
    };
  };

  validationSchema = () => {
    return Yup.object().shape({
      business_name: Yup.string().required('Campo requerido'),
      rfc: Yup.string()
        .required('Campo requerido')
        .matches(rfcRegExp, {
          message: 'Formato incorrecto de RFC'
        }),
      street: Yup.string().required('Campo requerido'),
      ext_number: Yup.string()
        .matches(/^[0-9]*$/, 'Ingresa solo numeros')
        .required('Campo requerido'),
      int_number: Yup.string().matches(/^[0-9]*$/, 'Ingresa solo numeros'),
      neighborhood: Yup.string().required('Campo requerido'),
      town: Yup.string().required('Campo requerido'),
      state: Yup.string().required('Campo requerido'),
      district: Yup.string().required('Campo requerido'),
      country: Yup.string().required('Campo requerido'),
      zip: Yup.string().required('Campo requerido')
    });
  };

  componentDidMount() {
    if (!StateStore.getSize()) {
      StatesAction.get_states({});
    } else {
      this._onChangeState();
    }
  }

  componentWillMount() {
    StateStore.addChangeListener(this._onChangeState);
  }

  componentWillUnmount() {
    StateStore.removeChangeListener(this._onChangeState);
  }

  _onChangeState = () => {
    let states = StateStore.getStates().map(state => ({
      value: state.id,
      label: state.name
    }));
    this.setState({ states }, () => {
      StateStore.removeChangeListener(this._onChangeState);
    });
  };

  submitData = (values: {}): void => {
    const { id, email, is_disabled } = this.props.legalEntity;
    const { business_name, rfc } = values;
    this.handleClickBillAction(
      new LegalEntity({
        id,
        email,
        business_name,
        rfc,
        is_disabled,
        address: values
      })
    );
  };

  handleClickBillAction = (values: {}): void => {
    this.props.handleClickBillAction(values);
  };

  renderNotShowBilling = () => {
    if (this.props.showNotBilling)
      return (
        <FlatButton
          type="button"
          onClick={() => {
            this.props.handleClickNotBillAction();
          }}
        >
          No facturar
        </FlatButton>
      );
    else return null;
  };

  renderSpinnerNextButton = () => {
    const { spinnerVisible, color } = this.props;
    return spinnerVisible ? (
      <Spinner />
    ) : (
      <Button color={color} type="submit">
        Continuar
      </Button>
    );
  };

  renderBillDataForm = (formik: any) => {
    const { errors, touched } = formik;
    const { states } = this.state;
    return (
      <Form>
        <Subtitle>Razón Social</Subtitle>
        <RenterApp.StyledValidatedInput>
          <RenterApp.StyledFmkInput
            type="text"
            name="business_name"
            placeholder="Razón Social"
            className={
              errors.business_name && touched.business_name ? 'has-error' : ''
            }
          />
          <RenterApp.StyledErrorMessage name="business_name" component="div" />
        </RenterApp.StyledValidatedInput>
        <Subtitle>RFC</Subtitle>
        <RenterApp.StyledValidatedInput>
          <RenterApp.StyledFmkInput
            type="text"
            name="rfc"
            placeholder="RFC"
            className={errors.rfc && touched.rfc ? 'has-error' : ''}
          />
          <RenterApp.StyledErrorMessage name="rfc" component="div" />
        </RenterApp.StyledValidatedInput>
        <Subtitle>Dirección</Subtitle>
        <RenterApp.StyledValidatedInput>
          <RenterApp.StyledFmkInput
            type="text"
            name="street"
            placeholder="Calle"
            className={errors.street && touched.street ? 'has-error' : ''}
          />
          <RenterApp.StyledErrorMessage name="street" component="div" />
        </RenterApp.StyledValidatedInput>
        <RenterApp.RowInputsContainer>
          <RenterApp.RowStyledInputContainer>
            <RenterApp.StyledNumberInput>
              <RenterApp.StyledFmkInput
                type="text"
                name="ext_number"
                placeholder="Número ext"
                className={
                  errors.ext_number && touched.ext_number ? 'has-error' : ''
                }
              />
              <RenterApp.StyledErrorMessage name="ext_number" component="div" />
            </RenterApp.StyledNumberInput>
          </RenterApp.RowStyledInputContainer>
          <Layout.Spacer />
          <RenterApp.RowStyledInputContainer>
            <RenterApp.StyledNumberInput>
              <RenterApp.StyledFmkInput
                type="text"
                name="int_number"
                placeholder="Número int"
                className={
                  errors.int_number && touched.int_number ? 'has-error' : ''
                }
              />
              <RenterApp.StyledErrorMessage name="int_number" component="div" />
            </RenterApp.StyledNumberInput>
          </RenterApp.RowStyledInputContainer>
        </RenterApp.RowInputsContainer>
        <RenterApp.StyledValidatedInput>
          <RenterApp.StyledFmkInput
            type="text"
            name="neighborhood"
            placeholder="Colonia"
            className={
              errors.neighborhood && touched.neighborhood ? 'has-error' : ''
            }
          />
          <RenterApp.StyledErrorMessage name="neighborhood" component="div" />
        </RenterApp.StyledValidatedInput>
        <RenterApp.StyledValidatedInput>
          <RenterApp.StyledFmkInput
            type="text"
            name="district"
            placeholder="Distrito"
            className={errors.district && touched.district ? 'has-error' : ''}
          />
          <RenterApp.StyledErrorMessage name="district" component="div" />
        </RenterApp.StyledValidatedInput>
        <RenterApp.StyledValidatedInput>
          <RenterApp.StyledFmkInput
            type="text"
            name="town"
            placeholder="Ciudad"
            className={errors.town && touched.town ? 'has-error' : ''}
          />
          <RenterApp.StyledErrorMessage name="town" component="div" />
        </RenterApp.StyledValidatedInput>
        <RenterApp.StyledValidatedInput>
          <RenterApp.StyledSelect
            component="select"
            name="state"
            className={errors.state && touched.state ? 'has-error' : ''}
          >
            <option hidden>Estado</option>
            {states.map(state => (
              <option key={state.value} value={state.label}>
                {state.label}
              </option>
            ))}
          </RenterApp.StyledSelect>
          <RenterApp.StyledErrorMessage name="state" component="div" />
        </RenterApp.StyledValidatedInput>
        <RenterApp.StyledValidatedInput>
          <RenterApp.StyledFmkInput
            type="text"
            name="country"
            placeholder="País"
            className={errors.country && touched.country ? 'has-error' : ''}
          />
          <RenterApp.StyledErrorMessage name="country" component="div" />
        </RenterApp.StyledValidatedInput>
        <RenterApp.StyledValidatedInput>
          <RenterApp.StyledFmkInput
            type="text"
            name="zip"
            placeholder="Código postal"
            className={errors.zip && touched.zip ? 'has-error' : ''}
          />
          <RenterApp.StyledErrorMessage name="zip" component="div" />
        </RenterApp.StyledValidatedInput>
        {this.renderSpinnerNextButton()}
        {this.renderNotShowBilling()}

        <LineHr visible={this.props.lineVisible} />
        <ErrorFocus />
      </Form>
    );
  };

  render() {
    return (
      <ContentForm visible={this.props.visible}>
        <DataConfirmText>Datos de facturación</DataConfirmText>
        <Formik
          validationSchema={this.validationSchema()}
          initialValues={this.defaultValues()}
          onSubmit={this.submitData}
          render={this.renderBillDataForm}
        />
      </ContentForm>
    );
  }
}
